import { useRef, useState, useEffect } from "react";
import { websiteUrl } from "../../const/constants";
import defaultMujallahImage from "../../Assets/images/mujallahimage1.svg";

const MujallahCarousel = ({
  mujallahs,
  selectedMujallah,
  handleMujallahSelect,
}) => {
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const itemsPerPage = 4; // Consistent with DiariesCarousel

  useEffect(() => {
    const filtered = mujallahs.filter(
      ({ mujjalah }) => mujjalah.id !== selectedMujallah?.id
    );
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    // For RTL: Start from page 0 (rightmost dot) which shows the latest items
    setCurrentPage(0);

    // Load initial items (latest first for RTL)
    loadPageItems(filtered, 0, totalPages);
  }, [mujallahs, selectedMujallah]);

  const loadPageItems = (filteredData, pageIndex, totalPages) => {
    // Ensure consistent ordering: sort by year descending (latest first)
    const sortedDesc = [...filteredData].sort((a, b) => {
      const ay = parseInt(a.mujjalah?.year, 10) || 0;
      const by = parseInt(b.mujjalah?.year, 10) || 0;
      return by - ay;
    });
    const startIndex = pageIndex * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const pageItems = sortedDesc.slice(startIndex, endIndex);

    setDisplayedItems(pageItems);
    // For RTL: hasMore means we can go to next page (left), hasPrev means we can go to previous page (right)
    setHasMore(pageIndex < totalPages - 1);
    setHasPrev(pageIndex > 0);
  };

  const handleNext = () => {
    if (hasMore) {
      const filtered = mujallahs.filter(
        ({ mujjalah }) => mujjalah.id !== selectedMujallah?.id
      );
      const totalPages = Math.ceil(filtered.length / itemsPerPage);
      const nextPage = currentPage + 1;

      setCurrentPage(nextPage);
      loadPageItems(filtered, nextPage, totalPages);
    }
  };

  const handlePrev = () => {
    if (hasPrev) {
      const filtered = mujallahs.filter(
        ({ mujjalah }) => mujjalah.id !== selectedMujallah?.id
      );
      const totalPages = Math.ceil(filtered.length / itemsPerPage);
      const prevPage = currentPage - 1;

      setCurrentPage(prevPage);
      loadPageItems(filtered, prevPage, totalPages);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return defaultMujallahImage;
    if (imagePath.startsWith("http")) return imagePath;
    return `${websiteUrl}/mujjalahImages/${imagePath.replace(/^\//, "")}`;
  };

  // const getPdfUrl = (pdfPath) => {
  //     if (!pdfPath) return null;
  //     if (pdfPath.startsWith("http")) return pdfPath;
  //     return `${websiteUrl}/mujallahPdfs/${pdfPath.replace(/^\//, "")}`;
  // };

  // const handlePdfClick = (pdfPath) => {
  //     const pdfUrl = getPdfUrl(pdfPath);
  //     if (pdfUrl) {
  //         window.open(pdfUrl, '_blank');
  //     }
  // };

  if (displayedItems.length === 0) return null;

  return (
    <div className="bg-primaryBG flex flex-col items-center gap-y-8 py-10 px-10 lg:px-[121px] rounded-3xl mt-12 mb-12 transition-all duration-300 hover:shadow-lg">
      <h1 className="font-nastaleeqKasheeda text-2xl sm:text-3xl md:text-4xl lg:text-4xl hover:text-primary transition-colors duration-300">
        دیگر دستیاب مجلّے
      </h1>

      <div className="relative w-full">
        {/* Left button - in RTL this moves to older items (next page) */}
        {hasMore && (
          <button
            onClick={handleNext}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm text-gray-700 shadow-xl rounded-full p-3 transition-all duration-300 hover:bg-white hover:text-primary hover:scale-110 hover:shadow-2xl border border-gray-200"
            aria-label="Go to older items"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Right button - in RTL this moves to newer items (previous page) */}
        {hasPrev && (
          <button
            onClick={handlePrev}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm text-gray-700 shadow-xl rounded-full p-3 transition-all duration-300 hover:bg-white hover:text-primary hover:scale-110 hover:shadow-2xl border border-gray-200"
            aria-label="Go to newer items"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}

        <div ref={containerRef} className="overflow-hidden scroll-smooth">
          <div className="flex w-full transition-all duration-300">
            <div className="flex flex-row-reverse flex-nowrap gap-6 min-w-full justify-center">
              {displayedItems.map(({ mujjalah }) => (
                <div
                  key={mujjalah.id}
                  className="flex flex-col gap-4 items-center cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg rounded-lg p-2"
                  onClick={() => handleMujallahSelect(mujjalah)}
                >
                  <img
                    src={getImageUrl(mujjalah?.image)}
                    alt={`Mujallah ${mujjalah.year}`}
                    className="h-[300px] w-[229px] object-contain rounded-lg transition-all duration-300 hover:shadow-xl"
                    loading="lazy"
                  />
                  <h2 className="text-lg sm:text-xl md:text-xl lg:text-2xl font-nastaleeqKasheeda hover:text-primary transition-colors duration-300">
                    {mujjalah.year}
                  </h2>
                  {mujjalah.saal && (
                    <p className="text-lg sm:text-xl md:text-xl lg:text-2xl text-[#4C4C4C] hover:text-primary/80 transition-colors duration-300">
                      {mujjalah.saal}
                    </p>
                  )}
                  {/* {mujjalah.pdf && (
                      <Buttons
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                              e.stopPropagation(); // Prevent triggering handleMujallahSelect
                              handlePdfClick(mujjalah.pdf);
                          }}
                      >
                          View PDF
                      </Button>
                  )} */}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dot indicators - RTL order (latest first) */}
      <div className="flex gap-2 mt-4 flex-row-reverse">
        {(() => {
          const filtered = mujallahs.filter(
            ({ mujjalah }) => mujjalah.id !== selectedMujallah?.id
          );
          const totalPages = Math.ceil(filtered.length / itemsPerPage);
          return Array.from({ length: totalPages }, (_, i) => (
            <div
              key={i}
              onClick={() => {
                setCurrentPage(i);
                loadPageItems(filtered, i, totalPages);
              }}
              className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 hover:scale-125 ${
                i === currentPage
                  ? "bg-primary shadow-lg shadow-primary/50"
                  : "bg-gray-400 hover:bg-gray-500"
              }`}
              aria-label={`Go to page ${i + 1}`}
            />
          ));
        })()}
      </div>
    </div>
  );
};

export default MujallahCarousel;
